import { Translate } from '@components'

import { TableColumn } from '@billy/design-system'
import { useMemo } from 'react'

import { SalesReportTableFormattedCurrency } from '../../elements/SalesReportTableFormattedCurrency'
import { SalesByCustomerReportTableAccessor } from '../../enums/salesByCustomerReportTableAccessor'
import { SalesByValueReportTableRow } from '../../types/salesByValueReportTableRow'
import { getSalesByValueReportAllSummary } from '../../utils/getSalesByValueReportAllSummary'
import { useSalesReportFilters } from '../useSalesReportFilters'
import { useTotalSales } from '../useTotalSales'
import { useSalesByCustomerReport } from './useSalesByCustomerReport'

export const useSalesByCustomerReportTableColumns = () => {
  const [{ shouldShowVat }] = useSalesReportFilters()
  const { salesByCustomerReport } = useSalesByCustomerReport()

  const salesByCustomerReportAllSummary = useMemo(
    () => getSalesByValueReportAllSummary(salesByCustomerReport),
    [salesByCustomerReport],
  )

  const { grossTotalSales, netTotalSales } = useTotalSales(salesByCustomerReportAllSummary)

  const baseColumns: TableColumn<SalesByValueReportTableRow>[] = useMemo(
    () => [
      {
        accessor: SalesByCustomerReportTableAccessor.CustomerName,
        Footer: <Translate value="sales_report.sales_by_customer.table.footer.total_sales" />,
        Header: <Translate value="sales_report.sales_by_customer.table.column.customer" />,
        size: 'm',
        truncate: true,
      },
      {
        accessor: SalesByCustomerReportTableAccessor.NetTotal,
        alignment: 'right',
        Cell: ({ value }) => <SalesReportTableFormattedCurrency value={value} />,
        Footer: netTotalSales,
        Header: <Translate value="sales_report.sales_by_customer.table.column.net_total" />,
        size: 'xs',
      },
      {
        accessor: SalesByCustomerReportTableAccessor.GrossTotal,
        alignment: 'right',
        Cell: ({ value }) => <SalesReportTableFormattedCurrency value={value} />,
        Header: <Translate value="sales_report.sales_by_customer.table.column.gross_total" />,
        Footer: grossTotalSales,
        hidden: !shouldShowVat,
        size: 'xs',
      },
    ],
    [grossTotalSales, netTotalSales, shouldShowVat],
  )

  return baseColumns.filter((column) => !column.hidden)
}
